import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, MessageCircle } from "lucide-react";
import { waLink } from "@/lib/api";
import { useContent } from "@/lib/useContent";
import { usePageMeta } from "@/lib/seo";

export default function BlogPost() {
  const { slug } = useParams();
  const blogs = useContent("blogs");
  const post = blogs.find((b) => b.slug === slug);
  usePageMeta(post?.title, post?.excerpt);
  if (!post) return <Navigate to="/blog" replace />;
  const related = blogs.filter((b) => b.slug !== slug).slice(0, 3);

  return (
    <div className="bg-white">
      <section className="relative h-[420px]">
        <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#051A2E]/90 via-black/40 to-black/20" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-14">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#E3BA45] font-bold">{post.category}</div>
          <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white mt-3 leading-tight" data-testid="blog-post-title">{post.title}</h1>
          <div className="text-white/70 text-sm mt-4">{post.date} · {post.read}</div>
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A2E4D] mb-10 hover:gap-3 transition-all" data-testid="blog-back-link"><ArrowLeft size={15} /> All articles</Link>
        {post.content.map((p, i) => <p key={i} className="text-[#374151] leading-[1.9] mb-6 text-[15px]">{p}</p>)}
        <div className="mt-10 bg-[#0A2E4D] rounded-2xl p-8 text-center">
          <h3 className="font-serif-display text-2xl text-white">Planning a trip like this?</h3>
          <p className="text-slate-300 text-sm mt-2">Chat with a Makchester consultant — free advice, fast replies.</p>
          <a href={waLink(`Hello Makchester, I just read "${post.title}" and I'd like assistance.`)} target="_blank" rel="noreferrer" className="mk-btn-gold mt-5"><MessageCircle size={15} /> Chat on WhatsApp</a>
        </div>
      </article>

      <section className="bg-[#F9FAFB] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif-display text-2xl text-[#0A2E4D] mb-8">Keep reading</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {related.map((b) => (
              <Link key={b.slug} to={`/blog/${b.slug}`} className="mk-card overflow-hidden group">
                <div className="h-40 overflow-hidden"><img src={b.image} alt={b.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
                <div className="p-6">
                  <h3 className="font-serif-display text-lg text-[#0A2E4D] leading-snug">{b.title}</h3>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A2E4D]">Read <ArrowRight size={13} className="text-[#D4A62A]" /></div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
